import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Alert, Spinner, Table, Form, Tabs, Tab } from 'react-bootstrap';
import { useAuth } from '../context/AuthContext';
import { useApp } from '../context/AppContext';
import { useTheme } from '../context/ThemeContext';
import { useNavigate } from 'react-router-dom';
import StoryService from '../services/story';
import AppService from '../services/app';
import './Analytics.css';

const Analytics = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [analytics, setAnalytics] = useState(null);
  const [mauStats, setMauStats] = useState(null);
  const [selectedStoryId, setSelectedStoryId] = useState('all');
  const [selectedAppId, setSelectedAppId] = useState(null);
  const [activeTab, setActiveTab] = useState('stories');
  const [loadingMau, setLoadingMau] = useState(false);
  const { isAuthenticated } = useAuth();
  const { currentApp, apps } = useApp();
  const { darkMode } = useTheme();
  const navigate = useNavigate();

  useEffect(() => {
    if (isAuthenticated && apps && apps.length > 0) {
      // Set the selected app to the current app or the first app in the list
      setSelectedAppId(currentApp?.id || apps[0].id);
    }
  }, [isAuthenticated, apps, currentApp]);

  useEffect(() => {
    if (isAuthenticated && selectedAppId) {
      if (activeTab === 'stories') {
        fetchAnalytics();
      } else if (activeTab === 'mau') {
        fetchMauStats();
      }
    }
  }, [isAuthenticated, selectedAppId, selectedStoryId, activeTab]);

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      const data = await StoryService.getAnalytics(selectedAppId, selectedStoryId);
      setAnalytics(data);
      setError('');
    } catch (err) {
      setError('Failed to load analytics: ' + (err.response?.data || err.message));
    } finally {
      setLoading(false);
    }
  };

  const fetchMauStats = async () => {
    try {
      setLoadingMau(true);
      const data = await AppService.getMauStats(selectedAppId);
      
      console.log('MAU stats response:', data);
      
      // Make sure we're properly handling the response structure
      if (data && data.stats) {
        console.log('Setting MAU stats:', data.stats);
        setMauStats(data.stats);
      } else {
        console.error('Unexpected MAU stats response structure:', data);
        setError('Failed to load MAU stats: Unexpected response format');
      }
      
      setError('');
    } catch (err) {
      console.error('MAU stats error:', err);
      setError('Failed to load MAU stats: ' + (err.response?.data || err.message));
    } finally {
      setLoadingMau(false);
    }
  };

  const handleStoryChange = (e) => {
    setSelectedStoryId(e.target.value);
  };
  
  const handleAppChange = (e) => {
    setSelectedAppId(Number(e.target.value));
  };
  
  const handleTabChange = (tab) => {
    setActiveTab(tab);
  };

  if (loading) {
    return (
      <Container className="mt-5 text-center">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="mt-5">
        <Alert variant="danger">{error}</Alert>
      </Container>
    );
  }

  if (!analytics) {
    return (
      <Container className="mt-5">
        <Alert variant="info">No analytics data available.</Alert>
      </Container>
    );
  }

  return (
    <Container className="mt-4">
      <h1 className="mb-4">Analytics Dashboard</h1>
      
      {apps && apps.length > 0 && (
        <Form.Group className="mb-4">
          <Form.Label>Select App</Form.Label>
          <Form.Select
            value={selectedAppId || ''}
            onChange={handleAppChange}
          >
            {apps.map(app => (
              <option key={app.id} value={app.id}>
                {app.name}
              </option>
            ))}
          </Form.Select>
        </Form.Group>
      )}
      
      <Tabs
        activeKey={activeTab}
        onSelect={handleTabChange}
        className="mb-4"
      >
        <Tab eventKey="stories" title="Story Analytics">
          {analytics && (
            <>
              {analytics.stories && analytics.stories.length > 0 && (
                <Form.Group className="mb-4">
                  <Form.Label>Select Story</Form.Label>
                  <Form.Select
                    value={selectedStoryId}
                    onChange={handleStoryChange}
                  >
                    <option value="all">All Stories</option>
                    {analytics.stories.map(story => (
                      <option key={story.id} value={story.id}>
                        {story.text || `Story #${story.id}`}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              )}

              <Row className="mb-4">
                <Col md={3}>
                  <Card className="text-center h-100" bg={darkMode ? "dark" : "light"}>
                    <Card.Body>
                      <Card.Title>Total Stories</Card.Title>
                      <div className="analytics-number">{analytics.totalStories}</div>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={3}>
                  <Card className="text-center h-100" bg={darkMode ? "dark" : "light"}>
                    <Card.Body>
                      <Card.Title>Unique Opened</Card.Title>
                      <div className="analytics-number">{analytics.uniqueOpened}</div>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={3}>
                  <Card className="text-center h-100" bg={darkMode ? "dark" : "light"}>
                    <Card.Body>
                      <Card.Title>Unique Completed</Card.Title>
                      <div className="analytics-number">{analytics.uniqueCompleted}</div>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
              
              <Row className="mb-4">
                <Col md={3}>
                  <Card className="text-center h-100" bg={darkMode ? "dark" : "light"}>
                    <Card.Body>
                      <Card.Title>All Opened</Card.Title>
                      <div className="analytics-number">{analytics.totalOpened}</div>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={3}>
                  <Card className="text-center h-100" bg={darkMode ? "dark" : "light"}>
                    <Card.Body>
                      <Card.Title>All Completed</Card.Title>
                      <div className="analytics-number">{analytics.totalCompleted}</div>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>

              <Card className="mb-4" bg={darkMode ? "dark" : "light"}>
                <Card.Header>
                  <h2 className="h5 mb-0">Story View Statistics</h2>
                </Card.Header>
                <Card.Body>
                  {analytics.storyStats && analytics.storyStats.length > 0 ? (
                    <Table striped bordered hover responsive variant={darkMode ? "dark" : "light"}>
                      <thead>
                        <tr>
                          <th>Story</th>
                          <th>Unique Opened</th>
                          <th>Unique Completed</th>
                          <th>Total Opened</th>
                          <th>Total Completed</th>
                          <th>Created Date</th>
                          <th>Published</th>
                        </tr>
                      </thead>
                      <tbody>
                        {analytics.storyStats.map(story => (
                          <tr key={story.id}>
                            <td>{story.text || `Story #${story.id}`}</td>
                            <td>{story.uniqueOpened}</td>
                            <td>{story.uniqueCompleted}</td>
                            <td>{story.totalOpened}</td>
                            <td>{story.totalCompleted}</td>
                            <td>{formatDate(story.createdAt)}</td>
                            <td>{story.published ? 'Yes' : 'No'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  ) : (
                    <Alert variant="info">No story statistics available.</Alert>
                  )}
                </Card.Body>
              </Card>
            </>
          )}
        </Tab>
        
        <Tab eventKey="mau" title="Monthly Active Users">
          {loadingMau ? (
            <div className="text-center my-5">
              <Spinner animation="border" role="status">
                <span className="visually-hidden">Loading MAU data...</span>
              </Spinner>
            </div>
          ) : mauStats ? (
            <>
              <Row className="mb-4">
                <Col md={4}>
                  <Card className="text-center h-100" bg={darkMode ? "dark" : "light"}>
                    <Card.Body>
                      <Card.Title>Total Monthly Active Users</Card.Title>
                      <div className="analytics-number">
                        {mauStats.totalMau !== undefined && mauStats.totalMau !== null
                          ? mauStats.totalMau
                          : 'N/A'}
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
              
              <Row className="mb-4">
                <Col md={6}>
                  <Card bg={darkMode ? "dark" : "light"}>
                    <Card.Header>
                      <h2 className="h5 mb-0">MAU by Platform</h2>
                    </Card.Header>
                    <Card.Body>
                      {mauStats.platformBreakdown && Object.keys(mauStats.platformBreakdown).length > 0 ? (
                        <Table striped bordered hover responsive variant={darkMode ? "dark" : "light"}>
                          <thead>
                            <tr>
                              <th>Platform</th>
                              <th>Active Users</th>
                            </tr>
                          </thead>
                          <tbody>
                            {mauStats.platformBreakdown && Object.entries(mauStats.platformBreakdown).map(([platform, count]) => (
                              <tr key={platform}>
                                <td>{platform}</td>
                                <td>{count}</td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      ) : (
                        <Alert variant="info">No platform data available.</Alert>
                      )}
                    </Card.Body>
                  </Card>
                </Col>
                
                <Col md={6}>
                  <Card bg={darkMode ? "dark" : "light"}>
                    <Card.Header>
                      <h2 className="h5 mb-0">Monthly Trend</h2>
                    </Card.Header>
                    <Card.Body>
                      {mauStats.monthlyTrend && Array.isArray(mauStats.monthlyTrend) && mauStats.monthlyTrend.length > 0 ? (
                        <Table striped bordered hover responsive variant={darkMode ? "dark" : "light"}>
                          <thead>
                            <tr>
                              <th>Month</th>
                              <th>Active Users</th>
                            </tr>
                          </thead>
                          <tbody>
                            {mauStats.monthlyTrend && mauStats.monthlyTrend.map((item, index) => (
                              <tr key={index}>
                                <td>{`${item.month} ${item.year}`}</td>
                                <td>{item.count}</td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      ) : (
                        <Alert variant="info">No monthly trend data available.</Alert>
                      )}
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            </>
          ) : (
            <Alert variant="info">No MAU data available. Select an app to view MAU statistics.</Alert>
          )}
        </Tab>
      </Tabs>
    </Container>
  );
};

const formatDate = (dateString) => {
  const options = { year: 'numeric', month: 'short', day: 'numeric' };
  return new Date(dateString).toLocaleDateString(undefined, options);
};

export default Analytics;